import { useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import { EVENT_CATEGORIES, type AdminEvent, type EventCategory } from '@mds/shared/types/event';

import { useSession } from '@/auth/use-session';
import CalendarGrid from '@/components/events/calendar-grid';
import EventDetails from '@/components/events/event-details';
import EventForm from '@/components/events/event-form';
import FeedbackBuilder, { FeedbackAlwaysAsked } from '@/components/events/feedback-builder';
import FeedbackResults from '@/components/events/feedback-results';
import { QuestionSetEditor } from '@/components/events/form-builder';
import RegistrationFormBuilder, {
  RegistrationAlwaysAsked,
} from '@/components/events/registration-form-builder';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import { controlCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Switch from '@/components/ui/switch';
import Tabs from '@/components/ui/tabs';
import Select from '@mds/shared/ui/select';
import { Table, Row, Cell } from '@/components/ui/table';
import { SegmentedToggle, Toolbar } from '@/components/ui/toolbar';
import {
  blankEventDraft,
  draftOf,
  eventDateLabel,
  eventPriceLabel,
  eventRequestOf,
  eventStatusTone,
  eventWhen,
  isEventCapacityBelowRegistrations,
  isEventHasPayments,
  isEventHasRegistrations,
  monthKeyOf,
  monthOptions,
  eventSchema,
  type EventDraft,
} from '@/domain/events';
import { blankFeedbackDraft, type FeedbackDraft } from '@/domain/feedback';
import {
  questionCount,
  questionSetInputOf,
  questionSetSchema,
  type QuestionSetDraft,
} from '@/domain/form-builder';
import { defaultRegistrationDraft } from '@/domain/registrations';
import * as events from '@/events/events.api';
import { confirmLeave } from '@/hooks/use-unsaved';
import { inputToCents } from '@/lib/money';
import { useValidation } from '@/lib/validation';
import useDebounced from '@mds/shared/hooks/use-debounced';

const ALL = 'All';
/** A month of programme fits on one page; the calendar needs all of it at once. */
const PAGE_SIZE = 100;

/** The create dialog's three parts. The event is published with all of them in one request. */
type CreateTab = 'event' | 'registration' | 'feedback';
const CREATE_TABS: readonly { key: CreateTab; label: string }[] = [
  { key: 'event', label: 'Event' },
  { key: 'registration', label: 'Registration form' },
  { key: 'feedback', label: 'Feedback form' },
];

/** The open event's parts. Each form saves on its own, from the dialog footer. */
type DetailTab = 'details' | 'registration' | 'feedback' | 'responses';
const DETAIL_TABS: readonly { key: DetailTab; label: string }[] = [
  { key: 'details', label: 'Event' },
  { key: 'registration', label: 'Registration form' },
  { key: 'feedback', label: 'Feedback form' },
  { key: 'responses', label: 'Responses' },
];

/** "the Event and Registration form tabs" */
const tabsPhrase = (tabs: readonly { label: string }[]) => {
  const labels = tabs.map((t) => t.label);
  const list =
    labels.length <= 2
      ? labels.join(' and ')
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  return `the ${list} tab${labels.length > 1 ? 's' : ''}`;
};

const SORTS = [
  { value: 'createdAt:desc', label: 'Recently added' },
  { value: 'eventDate:asc', label: 'Earliest date first' },
  { value: 'eventDate:desc', label: 'Latest date first' },
  { value: 'title:asc', label: 'Title' },
];

/** The public events programme: the schedule, and what is published on it. */
export default function EventsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { can } = useSession();
  const mayManage = can('events.manage');

  const months = useMemo(() => monthOptions(), []);
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [month, setMonth] = useState(() => monthKeyOf(new Date()));
  const [category, setCategory] = useState(ALL);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('createdAt:desc');
  const settled = useDebounced(search);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('details');
  /** The detail footer a form tab draws its Save and Discard into. */
  const [formActions, setFormActions] = useState<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [draft, setDraft] = useState<EventDraft>(blankEventDraft);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Creating also writes the event's registration and feedback forms, from
  // two more tabs in the same dialog. Editing leaves each form to its own tab
  // in the event's detail dialog, where it saves on its own.
  const [createTab, setCreateTab] = useState<CreateTab>('event');
  const [formDraft, setFormDraft] = useState<QuestionSetDraft>(defaultRegistrationDraft);
  const [feedbackDraft, setFeedbackDraft] = useState<FeedbackDraft>(blankFeedbackDraft);
  /** Publish has been pressed at least once, so each tab may say it needs attention. */
  const [attempted, setAttempted] = useState(false);

  const [sortKey, direction] = sort.split(':') as [
    'eventDate' | 'createdAt' | 'title',
    'asc' | 'desc',
  ];

  const list = useQuery({
    queryKey: ['events', { month, category, settled, sort }],
    queryFn: () =>
      events.listEvents({
        limit: PAGE_SIZE,
        month,
        category: category === ALL ? undefined : (category as EventCategory),
        // The API refuses a blank or overlong search rather than ignoring it.
        q: settled.trim().slice(0, 100) || undefined,
        sort: sortKey,
        direction,
      }),
  });

  // The open event is re-read on its own, so editing shows the server's copy
  // rather than whatever the row it was opened from happened to hold.
  const detail = useQuery({
    queryKey: ['event', detailId],
    queryFn: () => events.getEvent(detailId as string),
    enabled: detailId !== null,
  });
  const event = detail.data ?? null;

  // An edit may not drop the seats below those already confirmed.
  const schema = useMemo(
    () => eventSchema(editing && event ? event.registeredSeats : 0),
    [editing, event],
  );
  const validation = useValidation(schema, draft);
  const formValidation = useValidation(questionSetSchema, formDraft);
  const feedbackValidation = useValidation(questionSetSchema, feedbackDraft);

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const pagination = list.data?.pagination;

  const refresh = async (id: string | null) => {
    await queryClient.invalidateQueries({ queryKey: ['events'] });
    if (id) await queryClient.invalidateQueries({ queryKey: ['event', id] });
  };

  const openDetail = (e: AdminEvent) => {
    setDetailId(e.id);
    setDetailTab('details');
    setProblem(null);
  };

  // A form tab with unsaved edits asks before it is switched away from or closed.
  const pickDetailTab = (tab: DetailTab) => {
    if (tab !== detailTab && confirmLeave()) setDetailTab(tab);
  };
  const closeDetail = () => {
    if (confirmLeave()) setDetailId(null);
  };

  const startCreate = () => {
    setDraft(blankEventDraft());
    setFormDraft(defaultRegistrationDraft());
    setFeedbackDraft(blankFeedbackDraft());
    setProblem(null);
    validation.reset();
    formValidation.reset();
    feedbackValidation.reset();
    setCreateTab('event');
    setAttempted(false);
    setCreating(true);
  };

  const startEdit = () => {
    if (!event) return;
    setDraft(draftOf(event));
    setProblem(null);
    validation.reset();
    setEditing(true);
  };

  /** Each create tab with the validation that guards it, in the order the tabs are drawn. */
  const createChecks: readonly { key: CreateTab; label: string; validation: typeof validation }[] =
    [
      { key: 'event', label: 'Event', validation },
      { key: 'registration', label: 'Registration form', validation: formValidation },
      { key: 'feedback', label: 'Feedback form', validation: feedbackValidation },
    ];

  /**
   * Publishing checks every tab before anything is sent. Every error on every
   * tab is revealed at once, and the dialog lands on the first tab that has
   * one — checked last, so the focus `check()` moves lands on a field that is
   * drawn.
   */
  const checkCreate = () => {
    setAttempted(true);
    const failing = createChecks.filter((c) => !c.validation.valid);
    const first = failing[0];
    if (!first) return true;
    setCreateTab(first.key);
    for (const c of createChecks) if (c !== first) c.validation.check();
    first.validation.check();
    setProblem(
      failing.length > 1
        ? `Fill in the highlighted fields on ${tabsPhrase(failing)} before publishing.`
        : first.key === 'event'
          ? 'Fill in the highlighted fields before publishing.'
          : `Every question on the ${first.label.toLowerCase()} needs to be complete before publishing.`,
    );
    return false;
  };

  const save = async () => {
    if (editing ? !validation.check() : !checkCreate()) return;

    setBusy(true);
    setProblem(null);
    try {
      const body = eventRequestOf(draft);
      if (editing && event) {
        await events.updateEvent(event.id, body);
        await refresh(event.id);
        setEditing(false);
      } else {
        const created = await events.createEvent({
          ...body,
          registrationForm: questionSetInputOf(formDraft),
          feedbackForm: {
            isEnabled: feedbackDraft.isEnabled,
            ...questionSetInputOf(feedbackDraft),
          },
        });
        await refresh(null);
        setCreating(false);
        setDetailId(created.id);
        setDetailTab('details');
      }
    } catch (e) {
      setProblem(
        isEventCapacityBelowRegistrations(e)
          ? 'Seats cannot go below the number already registered. Raise the figure, or cancel registrations first.'
          : e instanceof ApiError
            ? e.message
            : 'That did not save. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!event) return;
    setBusy(true);
    setProblem(null);
    try {
      await events.deleteEvent(event.id);
      await refresh(null);
      setDeleting(false);
      setDetailId(null);
    } catch (e) {
      // Not a failure to retry: the record has to stay while anyone holds a place or has paid.
      setProblem(
        isEventHasRegistrations(e)
          ? 'Some registrations for this event are not cancelled. Use Cancel all on the Attendees page, then delete the event.'
          : isEventHasPayments(e)
            ? 'A payment was recorded for this event, so its record stays for the books.'
            : e instanceof ApiError
              ? e.message
              : 'That did not delete. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const formOpen = creating || editing;

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={
          pagination
            ? `${pagination.total} ${pagination.total === 1 ? 'event' : 'events'}`
            : 'Programme'
        }
        action={
          <Toolbar>
            <SegmentedToggle
              value={view}
              options={['list', 'calendar'] as const}
              onChange={setView}
            />
            <Select
              value={month}
              onChange={setMonth}
              options={months}
              aria-label="Filter by month"
            />
            <Select
              value={category}
              onChange={setCategory}
              options={[
                { value: ALL, label: 'All categories' },
                ...EVENT_CATEGORIES.map((c) => ({ value: c, label: c })),
              ]}
              aria-label="Filter by category"
            />
            {/* Searched on the server, across title, summary and venue. */}
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events"
              aria-label="Search events"
              className={controlCls + ' w-[200px]'}
            />
            <Select value={sort} onChange={setSort} options={SORTS} aria-label="Sort events" />
            {mayManage && <Button onClick={startCreate}>Create event</Button>}
          </Toolbar>
        }
      >
        {list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the programme…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load the programme"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 && view === 'list' ? (
          <EmptyState
            title="Nothing on this month"
            hint="Try another month or category, or clear the search."
          />
        ) : view === 'list' ? (
          <Table dense head={['When', 'Event', 'Category', 'Space', 'Seats', 'Price', 'Status']}>
            {rows.map((e) => {
              const tone = eventStatusTone(e.status);
              return (
                <Row key={e.id} onClick={() => openDetail(e)}>
                  <Cell className="whitespace-nowrap">
                    <span className="block">{eventDateLabel(e.eventDate)}</span>
                    <span className="block text-xs text-ink-muted">{eventWhen(e)}</span>
                  </Cell>
                  <Cell className="font-semibold">{e.title}</Cell>
                  <Cell>{e.category}</Cell>
                  <Cell>{e.space}</Cell>
                  <Cell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs tracking-normal">
                        {e.registeredSeats}/{e.capacity}
                      </span>
                      <span className="h-2 w-16 overflow-hidden rounded-full border border-hairline bg-ink/10">
                        <span
                          className="block h-full rounded-full bg-brand"
                          style={{
                            width:
                              Math.min(100, Math.round((e.registeredSeats / e.capacity) * 100)) +
                              '%',
                          }}
                        />
                      </span>
                    </div>
                  </Cell>
                  <Cell className="font-semibold">{eventPriceLabel(e.price.amountMinor)}</Cell>
                  <Cell>
                    <Chip size="sm" className={'border ' + tone.cls}>
                      {tone.label}
                    </Chip>
                  </Cell>
                </Row>
              );
            })}
          </Table>
        ) : (
          <CalendarGrid month={month} events={rows} onPick={openDetail} />
        )}

        {/* A month of programme fits on one page in practice, and the calendar
            needs all of it at once — so say so rather than page a calendar. */}
        {pagination?.hasMore && (
          <p className="mt-5 mb-0 text-base text-ink-muted">
            Showing the first {rows.length} of {pagination.total}. Narrow the month or category to
            see the rest.
          </p>
        )}
      </Card>

      <Modal
        open={detailId !== null && !formOpen && !deleting}
        onClose={closeDetail}
        title={event?.title ?? 'Event'}
        footer={
          !event || detailTab === 'responses' ? undefined : detailTab !== 'details' ? (
            <div ref={setFormActions} className="contents" />
          ) : (
            <>
              <Button onClick={() => navigate(`/events/attendees?event=${event.id}`)}>
                Attendees
              </Button>
              {mayManage && (
                <>
                  <Button variant="quiet" onClick={startEdit}>
                    Edit event
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setProblem(null);
                      setDeleting(true);
                    }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </>
          )
        }
      >
        {detail.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the event…</p>
        ) : detail.isError || !event ? (
          <Banner>
            {detail.error instanceof ApiError
              ? detail.error.message
              : 'That event could not be read.'}
          </Banner>
        ) : (
          <>
            <Tabs
              label="Event sections"
              tabs={DETAIL_TABS}
              active={detailTab}
              onPick={pickDetailTab}
            />
            {detailTab === 'registration' ? (
              <RegistrationFormBuilder event={event} actionsSlot={formActions} />
            ) : detailTab === 'feedback' ? (
              <FeedbackBuilder eventId={event.id} actionsSlot={formActions} />
            ) : detailTab === 'responses' ? (
              <FeedbackResults eventId={event.id} eventTitle={event.title} />
            ) : (
              <>
                {problem && <Banner>{problem}</Banner>}
                <EventDetails event={event} />
              </>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={formOpen}
        onClose={() => {
          setCreating(false);
          setEditing(false);
        }}
        title={editing ? 'Edit event' : 'Create event'}
        footer={
          <>
            <Button onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Publish event'}
            </Button>
            <Button
              variant="quiet"
              disabled={busy}
              onClick={() => {
                setCreating(false);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </>
        }
      >
        {creating && (
          <Tabs
            label="Create event"
            tabs={CREATE_TABS}
            active={createTab}
            onPick={setCreateTab}
            flagged={(key) =>
              attempted && !createChecks.find((c) => c.key === key)?.validation.valid
            }
          />
        )}
        {problem && <Banner>{problem}</Banner>}
        {creating && createTab === 'registration' ? (
          <>
            <p className="m-0 text-base text-ink-secondary">
              Asked on the public registration page, and when you add a party from the attendee
              list. Every question here has to be complete before the event is published. It can
              change later from the event's Registration form tab.
            </p>
            <section className="flex flex-col gap-3">
              <h3 className="m-0 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
                Always asked
              </h3>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                <RegistrationAlwaysAsked priced={(inputToCents(draft.price) ?? 0) > 0} />
              </ul>
            </section>
            <section className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="m-0 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
                  Your questions
                </h3>
                <Chip>{questionCount(formDraft)} of 100</Chip>
              </div>
              <QuestionSetEditor
                draft={formDraft}
                onChange={setFormDraft}
                readOnly={false}
                field={formValidation.field}
                error={formValidation.error}
                hint="Optional. Asked after the details above. Group related questions under a section heading and they appear together."
              />
            </section>
          </>
        ) : creating && createTab === 'feedback' ? (
          <>
            <p className="m-0 text-base text-ink-secondary">
              When the event ends, every guest who checked in and left an email is sent a private
              link to this form. Guests are always asked for a rating and a comment, so it works as
              it is. Every question you add has to be complete before the event is published, and
              the form stays editable from the event's Feedback form tab until the first invitation
              goes out.
            </p>
            <div className="flex flex-col gap-1 rounded-xl border border-hairline bg-raised px-4 py-3">
              <Switch
                checked={feedbackDraft.isEnabled}
                label="Send invitations when the event ends"
                onChange={(isEnabled) => setFeedbackDraft({ ...feedbackDraft, isEnabled })}
              />
              <span className="text-sm text-ink-muted">
                {feedbackDraft.isEnabled
                  ? 'Guests who check in will be invited.'
                  : "Nothing is sent while this is off. It can be switched on later from the event's Feedback form tab."}
              </span>
            </div>
            <section className="flex flex-col gap-3">
              <h3 className="m-0 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
                Always asked
              </h3>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                <FeedbackAlwaysAsked />
              </ul>
            </section>
            <section className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="m-0 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
                  Your questions
                </h3>
                <Chip>{questionCount(feedbackDraft)} of 100</Chip>
              </div>
              <QuestionSetEditor
                draft={feedbackDraft}
                onChange={setFeedbackDraft}
                readOnly={false}
                field={feedbackValidation.field}
                error={feedbackValidation.error}
                hint="Optional. Questions here come straight after the rating. Group related ones under a section heading — the venue, the speaker, what to run next — and guests see them together."
              />
            </section>
          </>
        ) : (
          <EventForm
            draft={draft}
            onChange={setDraft}
            known={editing && event ? event.media : undefined}
            field={validation.field}
          />
        )}
      </Modal>

      <Modal
        open={deleting && event !== null}
        onClose={() => setDeleting(false)}
        title="Delete this event"
        footer={
          <>
            <Button variant="danger" onClick={() => void remove()} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete event'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setDeleting(false)}>
              Keep it
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        <p className="m-0 text-base text-ink-secondary">
          The event, its gallery links and its cancelled registrations go for good. The pictures
          themselves stay in Files. An event with registrations that are not cancelled, or with a
          recorded payment, cannot be deleted.
        </p>
      </Modal>
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
    >
      {children}
    </p>
  );
}
